/**
 * PlanComparePage (F-201 / T-151-001, US-165) — the /compare route.
 *
 * ACs 1-3: the comparison page loads, makes the feature differences between
 * Essentials and Professional explicit, and recommends a plan from the
 * visitor's 3-question inputs. Rulings (dispatch 2026-10-08): prices come
 * from the plans API (useUsdPricing → GET api/usd-catalog/plans, fallback
 * mirror when offline) — never hardcoded; trial copy is 14-day
 * (FIRST_FRUITS_TRIAL_DAYS), no card required.
 *
 * AC-5: the trial CTA here starts the server trial (trialFlow →
 * POST api/usd-billing/trial/start); the Enterprise recommendation routes
 * through MeetingLinkCta (F-188) instead.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MeetingLinkCta from '../Shared/MeetingLinkCta';
import { useUsdPricing } from '../../hooks/useUsdPricing';
import {
  FIRST_FRUITS_TRIAL_DAYS,
  QuizAnswers,
  UsdPlan,
  UsdService,
  displayTierName,
  formatUsd,
  recommendTier,
} from '../../services/usdPricingModel';
import { startFreeTrial, TrialStartOutcome } from '../../services/trialFlow';

const PLAN_ORDER = ['freemium', 'essentials', 'professional', 'enterprise'];
const COMPARE_TIERS = ['essentials', 'professional'] as const;

const QUIZ_QUESTIONS: {
  id: keyof QuizAnswers;
  question: string;
  options: { label: string; value: string }[];
}[] = [
  {
    id: 'units',
    question: 'How many units do you manage?',
    options: [
      { label: '1 unit', value: '1' },
      { label: '2–5 units', value: '2-5' },
      { label: '6–20 units', value: '6-20' },
      { label: '21+ units', value: '21+' },
    ],
  },
  {
    id: 'services',
    question: 'What do you need most?',
    options: [
      { label: 'Compliance tracking', value: 'compliance' },
      { label: 'Tenant management', value: 'tenant' },
      { label: 'Legal documents + evictions', value: 'legal' },
      { label: 'All of the above', value: 'all' },
    ],
  },
  {
    id: 'evictions',
    question: 'How often do you file evictions?',
    options: [
      { label: 'Rarely or never', value: 'none' },
      { label: 'A few per year', value: 'sometimes' },
      { label: 'Regularly', value: 'frequent' },
    ],
  },
];

const FAQ: { q: string; a: string }[] = [
  {
    q: 'Is the trial really free?',
    a: `Yes — ${FIRST_FRUITS_TRIAL_DAYS} days of full access, no credit card. The FastSpring checkout only opens when you choose to subscribe.`,
  },
  {
    q: 'What happens when the trial ends?',
    a: 'Your account switches to the free Homestead tier — nothing is deleted while you decide, and you can export your data at any time.',
  },
  {
    q: 'Can I change plans later?',
    a: 'Yes. Upgrades take effect immediately (priced from the live catalog); downgrades apply at the next billing cycle.',
  },
  {
    q: 'Are service calls per unit charged on top?',
    a: 'Every paid tier includes a monthly service-call allowance. Beyond it you can top up per unit from the pricing page — the gleaning margin keeps small portfolios cheap.',
  },
];

function serviceNamesFor(
  planId: string,
  plan: UsdPlan | undefined,
  services: UsdService[]
): string[] {
  if (Array.isArray(plan?.unlockedServices) && plan!.unlockedServices!.length > 0) {
    return plan!.unlockedServices!.map(
      (t) => services.find((s) => s.type === t)?.name || t
    );
  }
  const planIdx = PLAN_ORDER.indexOf(planId);
  return services
    .filter((s) => PLAN_ORDER.indexOf(s.planAccess) <= planIdx && PLAN_ORDER.indexOf(s.planAccess) > 0)
    .map((s) => s.name);
}

const PlanComparePage: React.FC = () => {
  const navigate = useNavigate();
  const { plans, services } = useUsdPricing();

  const [answers, setAnswers] = useState<Partial<QuizAnswers>>({});
  const [trialBusy, setTrialBusy] = useState<string | null>(null);
  const [trialOutcome, setTrialOutcome] = useState<TrialStartOutcome | null>(null);

  const essentials = plans.find((p) => p.id === 'essentials');
  const professional = plans.find((p) => p.id === 'professional');
  const planById = (id: string) => plans.find((p) => p.id === id);

  const allAnswered = !!(answers.units && answers.services && answers.evictions);
  const recommendation = allAnswered
    ? recommendTier(answers as QuizAnswers)
    : null;

  const handleTrial = async (tierId: string) => {
    setTrialBusy(tierId);
    setTrialOutcome(null);
    const outcome = await startFreeTrial(tierId);
    setTrialBusy(null);
    setTrialOutcome(outcome);
    if (outcome.status === 'needs-signup') {
      navigate(`/signup?plan=${tierId}`);
    }
  };

  const planPrice = (plan: UsdPlan | undefined): string =>
    plan && plan.price > 0 ? `${formatUsd(plan.price)}/mo` : 'Contact sales';

  const rows: { key: string; label: string; essentials: string; professional: string }[] = [
    {
      key: 'price',
      label: 'Monthly price',
      essentials: planPrice(essentials),
      professional: planPrice(professional),
    },
    {
      key: 'units',
      label: 'Properties included',
      essentials: essentials ? `${essentials.maxUnits}` : '—',
      professional: professional ? `${professional.maxUnits}` : '—',
    },
    {
      key: 'allowance',
      label: 'Service calls included / month',
      essentials:
        essentials?.serviceCallAllowance != null
          ? `${essentials.serviceCallAllowance}`
          : '—',
      professional:
        professional?.serviceCallAllowance != null
          ? `${professional.serviceCallAllowance}`
          : '—',
    },
    {
      key: 'services',
      label: 'Per-use services unlocked',
      essentials: serviceNamesFor('essentials', essentials, services).join(', ') || '—',
      professional: serviceNamesFor('professional', professional, services).join(', ') || '—',
    },
    {
      key: 'support',
      label: 'Support',
      essentials: 'Standard',
      professional: 'Priority',
    },
    {
      key: 'trial',
      label: 'Free trial, no credit card',
      essentials: `${FIRST_FRUITS_TRIAL_DAYS} days`,
      professional: `${FIRST_FRUITS_TRIAL_DAYS} days`,
    },
    {
      key: 'billing',
      label: 'Billing',
      essentials: 'Self-serve (FastSpring)',
      professional: 'Self-serve (FastSpring)',
    },
  ];

  const recPlan = recommendation ? planById(recommendation.tierId) : undefined;
  const recPrice: string | null = recommendation
    ? recommendation.tierId === 'enterprise'
      ? null
      : recPlan && recPlan.price > 0
        ? `${formatUsd(recPlan.price)}/mo`
        : recommendation.monthlyUsd !== null
          ? `${formatUsd(recommendation.monthlyUsd)}/mo`
          : null
    : null;

  return (
    <div className="pricing-page" data-testid="compare-page">
      <header className="pricing-header">
        <h1>Compare Plans</h1>
        <p>
          One flat price per tier — {FIRST_FRUITS_TRIAL_DAYS}-day full-access trial, no
          credit card. Prices load live from the catalog.
        </p>
      </header>

      <section className="pricing-topup" data-testid="compare-table" aria-label="Plan comparison">
        <table>
          <thead>
            <tr>
              <th scope="col">Feature</th>
              {COMPARE_TIERS.map((tierId) => (
                <th key={tierId} scope="col">
                  {displayTierName(planById(tierId) || { id: tierId, name: tierId })}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key}>
                <th scope="row">{row.label}</th>
                <td data-testid={`compare-row-${row.key}-essentials`}>{row.essentials}</td>
                <td data-testid={`compare-row-${row.key}-professional`}>{row.professional}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {/* Trial CTAs per column (AC-4/AC-5: server trial, no card) */}
      <div className="products-container">
        {COMPARE_TIERS.map((tierId) => (
          <div
            key={tierId}
            className="product-section"
            data-testid={`compare-tier-${tierId}`}
          >
            <div className="product-header">
              <h2>{displayTierName(planById(tierId) || { id: tierId, name: tierId })}</h2>
            </div>
            <div className="price">
              <span className="amount">
                {planById(tierId) && planById(tierId)!.price > 0
                  ? formatUsd(planById(tierId)!.price)
                  : 'Contact'}
              </span>
              <span className="period">/month</span>
            </div>
            <button
              className="subscribe-btn trial"
              onClick={() => handleTrial(tierId)}
              disabled={trialBusy !== null}
              data-testid={`compare-trial-${tierId}`}
            >
              {trialBusy === tierId
                ? 'Starting your trial…'
                : `Start my ${FIRST_FRUITS_TRIAL_DAYS}-day free trial`}
            </button>
          </div>
        ))}
      </div>

      {trialOutcome && trialOutcome.status !== 'started' && trialOutcome.status !== 'needs-signup' && (
        <div className="pricing-error" role="alert" data-testid="compare-trial-error">
          {trialOutcome.message || 'Could not start your trial — please try again.'}
        </div>
      )}
      {trialOutcome && trialOutcome.status === 'started' && (
        <div className="pricing-notice" role="status" data-testid="compare-trial-started">
          Your {FIRST_FRUITS_TRIAL_DAYS}-day free trial is live — no credit card required.
        </div>
      )}

      {/* 3-question quiz → recommendation (AC3) */}
      <section className="pricing-topup" data-testid="compare-quiz" aria-label="Which plan fits me">
        <h2>Which plan fits me? 3 questions</h2>
        {QUIZ_QUESTIONS.map((q) => (
          <fieldset key={q.id} data-testid={`compare-quiz-${q.id}`}>
            <legend>{q.question}</legend>
            {q.options.map((opt) => (
              <label key={opt.value}>
                <input
                  type="radio"
                  name={q.id}
                  value={opt.value}
                  checked={answers[q.id] === opt.value}
                  onChange={() =>
                    setAnswers((prev) => ({ ...prev, [q.id]: opt.value }))
                  }
                />
                {opt.label}
              </label>
            ))}
          </fieldset>
        ))}

        {recommendation && (
          <div data-testid="compare-recommendation">
            <h3>We recommend: {recommendation.tierName}</h3>
            <p data-testid="compare-recommendation-price">
              {recPrice !== null ? recPrice : 'Pricing scoped to your portfolio — talk to us.'}
            </p>
            <p>{recommendation.reason}</p>
            {recommendation.tierId === 'enterprise' ? (
              <MeetingLinkCta tier="enterprise" campaign="plan-compare" label="Book a call" />
            ) : (
              <button
                className="subscribe-btn trial"
                onClick={() => handleTrial(recommendation.tierId)}
                disabled={trialBusy !== null}
                data-testid="compare-recommendation-cta"
              >
                {trialBusy === recommendation.tierId
                  ? 'Starting your trial…'
                  : `Start my ${FIRST_FRUITS_TRIAL_DAYS}-day free trial`}
              </button>
            )}
          </div>
        )}
      </section>

      <section data-testid="compare-faq" aria-label="Frequently asked questions">
        <h2>Common questions</h2>
        {FAQ.map((item) => (
          <details key={item.q}>
            <summary>{item.q}</summary>
            <p>{item.a}</p>
          </details>
        ))}
      </section>
    </div>
  );
};

export default PlanComparePage;
